/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { ComponentLike } from '@glint/template';
import CampaignModel from 'cio/models/campaign';
import { JourneyMetrics } from 'cio/utils/journey-metrics';
import { Measure, MetricDataPoint, Periods } from 'cio/utils/metrics/common';
import { MeasureType } from 'cio/utils/metrics/measure';
import { Metric } from 'cio/utils/metrics/models/metric';
import { Grouping } from 'cio/utils/metrics/resolution';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import type { Options } from 'highcharts';
import DeliveryMetricsSentTooltip from 'cio/components/campaign-overview-page/sparklines-section/metric-card/tooltip-content/delivery-metrics/sent';
import DeliveryMetricsOpenedTooltip from 'cio/components/campaign-overview-page/sparklines-section/metric-card/tooltip-content/delivery-metrics/opened';
import DeliveryMetricsClickedTooltip from 'cio/components/campaign-overview-page/sparklines-section/metric-card/tooltip-content/delivery-metrics/clicked';
import DeliveryMetricsConvertedTooltip from 'cio/components/campaign-overview-page/sparklines-section/metric-card/tooltip-content/delivery-metrics/converted';

interface CampaignOverviewPageSparklinesSectionArgs {
  campaign: CampaignModel;
  timeRange: DateRange;
  timePeriod: Periods;
  grouping: Grouping;
  campaignDeliveryMetrics: Metric | null;
  campaignJourneyMetrics: JourneyMetrics | null;
  isLoadingMetrics: boolean;
  channels: string[];
}

interface DeliveryMetricsCard {
  measure: Measure;
  measureType: MeasureType;
  title: string;
  graphTitle: string;
  graphColor: string;
  graphGradient: string;
  isRate: boolean;
  chartOptions: Partial<Options> | null;
  helpTooltipComponent?: ComponentLike<any>;
  data: MetricDataPoint[] | undefined | null;
  periodTotal: number | undefined | null;
}

const DELIVERY_METRICS_CARDS: Partial<Record<Measure, DeliveryMetricsCard>> = {
  sent: {
    measure: 'sent',
    measureType: MeasureType.DeliveryMetrics,
    title: 'Sent',
    graphTitle: 'Deliveries sent',
    graphColor: 'plum-900',
    graphGradient: 'gradient-plum-300',
    isRate: false,
    chartOptions: null,
    helpTooltipComponent: DeliveryMetricsSentTooltip,
    data: null,
    periodTotal: null,
  },
  opened: {
    measure: 'opened',
    measureType: MeasureType.DeliveryMetrics,
    title: 'Opened',
    graphTitle: 'Open rate',
    graphColor: 'plum-900',
    graphGradient: 'gradient-plum-300',
    isRate: true,
    chartOptions: {
      time: { timezone: 'Etc/GMT+4' },
      xAxis: { startOfWeek: 0 },
    },
    helpTooltipComponent: DeliveryMetricsOpenedTooltip,
    data: null,
    periodTotal: null,
  },
  clicked: {
    measure: 'clicked',
    measureType: MeasureType.DeliveryMetrics,
    title: 'Clicked',
    graphTitle: 'Click rate',
    graphColor: 'plum-900',
    graphGradient: 'gradient-plum-300',
    isRate: true,
    chartOptions: {
      time: { timezone: 'Etc/GMT+4' },
      xAxis: { startOfWeek: 0 },
    },
    helpTooltipComponent: DeliveryMetricsClickedTooltip,
    data: null,
    periodTotal: null,
  },
  converted: {
    measure: 'converted',
    measureType: MeasureType.DeliveryMetrics,
    title: 'Converted',
    graphTitle: 'Conversion rate',
    graphColor: 'plum-900',
    graphGradient: 'gradient-plum-300',
    isRate: true,
    chartOptions: null,
    helpTooltipComponent: DeliveryMetricsConvertedTooltip,
    data: null,
    periodTotal: null,
  },
  failed: {
    measure: 'failed',
    measureType: MeasureType.DeliveryMetrics,
    title: 'Failed',
    graphTitle: 'Failed rate',
    graphColor: 'plum-900',
    graphGradient: 'gradient-plum-300',
    isRate: true,
    chartOptions: null,
    data: null,
    periodTotal: null,
  },
};

export default class CampaignOverviewSparklinesSectionPage extends Component<CampaignOverviewPageSparklinesSectionArgs> {
  get cards(): DeliveryMetricsCard[] {
    let data = this.args.campaignDeliveryMetrics;

    return (
      this.args.campaign.isWebhookTriggered
        ? []
        : [
            DELIVERY_METRICS_CARDS.sent,
            DELIVERY_METRICS_CARDS.opened,
            DELIVERY_METRICS_CARDS.clicked,
            DELIVERY_METRICS_CARDS.converted,
          ]
    ).map((card: DeliveryMetricsCard) => {
      return {
        ...card,
        data: card.isRate ? data?.[card.measure]?.rates : data?.[card.measure]?.totals,
        periodTotal: card.isRate ? data?.[card.measure]?.rate : data?.[card.measure]?.total,
      };
    });
  }
}
